import React, { useContext, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  ShieldCheck,
  Shield,
  Car,
  CreditCard,
  Sparkles,
  Loader2,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import loginSuccess from "../context/actions/auth/loginSuccess";
import { GlobalContext } from "../context/Provider";
import henceforthApi from "../utils/henceforthApi";
import { Button } from "../components/ui/button";

const REMEMBER_KEY = "readyshare_admin_email";

interface LoginProps {
  logOutNow?: () => void;
  navigate?: any;
  location?: any;
}

const Login = ({ logOutNow, navigate, location }: LoginProps) => {
  const { authDispatch } = useContext(GlobalContext);

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(true);

  // Load remembered email if previously stored
  useEffect(() => {
    try {
      const savedEmail = localStorage.getItem(REMEMBER_KEY);
      if (savedEmail) {
        setEmail(savedEmail);
        setRememberMe(true);
      }
    } catch {
      // storage unavailable; ignore
    }
  }, []);

  const onSubmitForm = () => {
    if (loading) return;
    setLoading(true);

    // Trim always; lowercase only when it looks like an email so
    // case-sensitive usernames are not altered.
    const trimmed = email.trim();
    const cleanEmail = trimmed.includes("@") ? trimmed.toLowerCase() : trimmed;

    const items = {
      email: cleanEmail,
      password: password,
      language: "ENGLISH",
    };

    henceforthApi.Auth.login(items)
      .then((res: any) => {
        // Only remember the email after a successful login
        try {
          if (rememberMe && cleanEmail) {
            localStorage.setItem(REMEMBER_KEY, cleanEmail);
          } else {
            localStorage.removeItem(REMEMBER_KEY);
          }
        } catch {
          // storage unavailable; ignore
        }
        loginSuccess(res.data)(authDispatch);
      })
      .catch((err: any) => {
        console.error("Login error:", err);
        const response = err?.response?.body;
        const errorDesc =
          response?.error_description ||
          response?.message ||
          "Authentication failed. Please verify your credentials.";

        toast.error(errorDesc);

        if (response?.error && String(response.error).includes("unauthorized")) {
          if (typeof logOutNow === "function") {
            logOutNow();
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row bg-slate-50 font-sans overflow-x-hidden selection:bg-emerald-100 selection:text-emerald-900">
      {/* ================= Left Showcase Pane (Tailwind CSS) ================= */}
      <section className="hidden lg:flex lg:w-1/2 xl:w-[55%] relative flex-col justify-between p-10 xl:p-14 text-white overflow-hidden bg-gradient-to-br from-[#043324] via-[#087F5B] to-[#064e3b] shadow-2xl z-0">
        {/* Decorative backdrop glow effects */}
        <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#b7f7d4]/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-emerald-300/10 blur-3xl" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.15)_1px,transparent_1px)] [background-size:24px_24px] opacity-30" />

        {/* Top Header */}
        <header className="relative z-10 flex items-center gap-3.5">
          <div className="h-16 w-16 rounded-xl bg-white shadow-md shadow-black/20 flex items-center justify-center shrink-0 transition-transform duration-200 hover:-translate-y-0.5">
            <img src={logo} alt="ReadyShare Logo" className="h-full w-full object-contain" />
          </div>
          <div>
            <h2 className="text-4xl font-bold tracking-tight text-black m-0 leading-tight">
              <span className="text-primary">Ready</span>Share
            </h2>
          </div>
        </header>

        {/* Central Hero & Value Propositions */}
        <div className="relative z-10 my-auto py-8 max-w-lg">

          <h1 className="text-3xl xl:text-4xl font-extrabold leading-tight tracking-tight text-white mb-4">
            Smart Carpooling &amp;{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#b7f7d4] to-white">
              Fleet Operations
            </span>
          </h1>

          <p className="text-emerald-100/80 text-sm leading-relaxed mb-8">
            Supercharge ride-sharing logistics, real-time dispatching, driver
            compliance, and automated Stripe payout flows from one unified
            control center.
          </p>

          <div className="flex flex-col gap-3">
            <div className="group flex items-center gap-3.5 p-3.5 rounded-xl bg-white/[0.08] hover:bg-white/[0.14] backdrop-blur-md border border-white/10 hover:border-white/20 transition-all duration-200 hover:translate-x-1">
              <div className="h-10 w-10 rounded-lg bg-[#b7f7d4]/15 text-[#b7f7d4] flex items-center justify-center shrink-0">
                <Car size={20} />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white m-0">
                  Real-Time Rides &amp; Routes
                </h4>
                <p className="text-xs text-emerald-100/70 m-0 mt-0.5 leading-normal">
                  Monitor live vehicle bookings, scheduled trips, and active passengers.
                </p>
              </div>
            </div>

            <div className="group flex items-center gap-3.5 p-3.5 rounded-xl bg-white/[0.08] hover:bg-white/[0.14] backdrop-blur-md border border-white/10 hover:border-white/20 transition-all duration-200 hover:translate-x-1">
              <div className="h-10 w-10 rounded-lg bg-[#b7f7d4]/15 text-[#b7f7d4] flex items-center justify-center shrink-0">
                <ShieldCheck size={20} />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white m-0">
                  Safety &amp; Driver Verification
                </h4>
                <p className="text-xs text-emerald-100/70 m-0 mt-0.5 leading-normal">
                  Automated license screening, vehicle checks, and incident resolution.
                </p>
              </div>
            </div>

            <div className="group flex items-center gap-3.5 p-3.5 rounded-xl bg-white/[0.08] hover:bg-white/[0.14] backdrop-blur-md border border-white/10 hover:border-white/20 transition-all duration-200 hover:translate-x-1">
              <div className="h-10 w-10 rounded-lg bg-[#b7f7d4]/15 text-[#b7f7d4] flex items-center justify-center shrink-0">
                <CreditCard size={20} />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-white m-0">
                  Transparent Payments &amp; Analytics
                </h4>
                <p className="text-xs text-emerald-100/70 m-0 mt-0.5 leading-normal">
                  Automated commission tracking, driver payouts, and revenue charts.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Status Footer */}
        <footer className="relative z-10 flex items-center justify-between pt-6 border-t border-white/10 text-xs text-emerald-100/70">
          <div className="inline-flex items-center gap-2 font-medium text-[#b7f7d4]">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            <span>Operational • ReadyShare</span>
          </div>
          <div>&copy; {new Date().getFullYear()} ReadyShare Inc.</div>
        </footer>
      </section>

      {/* ================= Right Authentication Pane (Tailwind CSS) ================= */}
      <section className="flex-1 flex flex-col justify-center items-center px-4 py-8 sm:px-8 lg:px-12 bg-white min-h-screen">
        <div className="w-full max-w-[420px] mx-auto">
          {/* Mobile Only Brand Header */}
          <div className="lg:hidden flex flex-col items-center mb-6 text-center">
            <div className="h-14 w-14 rounded-2xl bg-white p-2 shadow-md border border-slate-200 mb-2.5 flex items-center justify-center">
              <img src={logo} alt="ReadyShare Logo" className="h-full w-full object-contain" />
            </div>
            <h2 className="text-2xl font-extrabold text-[#087F5B] tracking-tight m-0">
              ReadyShare
            </h2>
            <p className="text-xs text-slate-500 font-medium mt-0.5">Admin Portal</p>
          </div>

          {/* Form Header */}
          <div className="mb-6 text-left">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight m-0">
              Welcome Back
            </h1>
            <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
              Enter your administrative credentials to manage platform operations.
            </p>
          </div>

          {/* Sign In Form */}
          <form
            className="space-y-4"
            onSubmit={(e: React.FormEvent) => {
              e.preventDefault();
              onSubmitForm();
            }}
          >
            {/* Username / Email */}
            <div className="space-y-1.5 text-left">
              <label
                htmlFor="rs-email"
                className="block text-xs font-semibold text-slate-700 tracking-wide uppercase"
              >
                Email
              </label>
              <div className="relative flex items-center group">
                <span className="absolute left-3.5 text-slate-400 group-focus-within:text-[#087F5B] transition-colors pointer-events-none">
                  <Mail size={18} />
                </span>
                <input
                  id="rs-email"
                  type="text"
                  className="w-full h-11 pl-10 pr-3.5 bg-slate-50 hover:bg-slate-50/80 focus:bg-white border border-slate-200 focus:border-[#087F5B] focus:ring-2 focus:ring-[#087F5B]/15 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 transition-all outline-none shadow-sm"
                  placeholder="admin@readyshare.com"
                  name="email"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEmail(e.target.value)
                  }
                  required
                  autoFocus
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5 text-left">
              <label
                htmlFor="rs-password"
                className="block text-xs font-semibold text-slate-700 tracking-wide uppercase"
              >
                Password
              </label>
              <div className="relative flex items-center group">
                <span className="absolute left-3.5 text-slate-400 group-focus-within:text-[#087F5B] transition-colors pointer-events-none">
                  <Lock size={18} />
                </span>
                <input
                  id="rs-password"
                  type={showPassword ? "text" : "password"}
                  className="w-full h-11 pl-10 pr-11 bg-slate-50 hover:bg-slate-50/80 focus:bg-white border border-slate-200 focus:border-[#087F5B] focus:ring-2 focus:ring-[#087F5B]/15 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 transition-all outline-none shadow-sm"
                  placeholder="Enter your password"
                  name="password"
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPassword(e.target.value)
                  }
                  required
                  autoComplete="current-password"
                />
                <Button
                  type="button"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700 rounded-md hover:bg-slate-200/50 transition-colors focus:outline-none"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  variant="ghost"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </Button>
              </div>
            </div>


            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full h-11 sm:h-12 inline-flex items-center justify-center gap-2 rounded-lg text-sm sm:text-base font-semibold !text-white !bg-[#087F5B] hover:!bg-[#066749] active:scale-[0.99] shadow-md shadow-[#087F5B]/25 hover:shadow-lg hover:shadow-[#087F5B]/35 transition-all duration-200 disabled:!opacity-50 disabled:!cursor-not-allowed disabled:!shadow-none disabled:active:scale-100 !border-0 cursor-pointer mt-4"
              disabled={loading || !email.trim() || !password}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin text-white" />
                  <span>Authenticating...</span>
                </>
              ) : (
                <>
                  <span>Sign In to Dashboard</span>
                  <ArrowRight size={18} />
                </>
              )}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Login;